import { Button, Divider, Spin, Tooltip } from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { flyToGeometry } from "../../../../components/VMRS/ExploreArchive/ultis";
import UnifiedDynamicForm from "../../../../components/VMRS/ExploreTasking/components/form/UnifiedDynamicForm";
import { AOI_FEATURE_KEY } from "../../../../components/VMRS/MapAdapter/plugins/aoi/consts";
import { WorkflowStep } from "../../../../components/VMRS/Order/Tasking/types/workflow";
import { ESttausCode } from "../../../../enums";
import { showError, showSuccess } from "../../../../helpers/toast";
import ContentLayoutManagement from "../../../../layouts/ContentLayoutManagement";
import HeaderContent from "../../../../layouts/HeaderContent";
import
{
   getDetailOrderTaskingByUerAsync,
   getSchemaOrderAsync,
   updateOrderAsync,
} from "../../../../store/reducers/orderSlice";
import type { AppDispatch, RootState } from "../../../../store/store";

import CommonComponents from "../../../../components/Commons";
import ImagesMapVMRS from "../../../../components/VMRS/MapAdapter/ImagesMap";
import { changeSourceData } from "../../../../components/VMRS/MapAdapter/plugins/draw";
import
{
   clearFeatures,
   updateFeature,
} from "../../../../components/VMRS/MapAdapter/store/slices/drawSlice";
import
{
   changeBasemap,
   setBasemapList,
} from "../../../../components/VMRS/MapAdapter/store/slices/mapSlice";
import { BASEMAPS } from "../../../../components/VMRS/MapAdapter/utils/consts";
import useToggle from "../../../../hooks/useToggle";
import { DRAW_SOURCE_ID } from "../../../../components/VMRS/MapAdapter/plugins/draw/consts";
import { map } from "lodash";

export default function EditOrderTasking()
{
   const { t } = useTranslation();
   const { id } = useParams();
   const dispatch = useDispatch<AppDispatch>();
   const navigate = useNavigate();
   const location = useLocation();
   const [data, setData] = useState<any>({});
   const [schema, setSchema] = useState<any>({});
   const [loadingSchema, setLoadingSchema] = useState(true);
   const [loading, setLoading] = useState(false);
   const [formData, setFormData] = useState<Record<string, any>>({});
   const [engineMap, setEngineMap] = useState<maplibregl.Map | undefined>(
      undefined,
   );

   const {
      mapType,
      view: mapView,
      basemaps,
      selectedBasemap,
   } = useSelector((state: RootState) => state.map);
   const { features } = useSelector((state: RootState) => state.draw);

   const {
      open: openConfirm,
      toggle: toggleConfirm,
      shouldRender: shouldConfirm,
   } = useToggle();

   const getData = async () =>
   {
      try
      {
         const result = await dispatch(
            getDetailOrderTaskingByUerAsync(id as string),
         );
         const res = result as any;
         setData(res?.payload?.data?.data ?? {});

         if (res?.payload?.data?.data?.items?.[0]?.order_payload?.geometry)
         {
            const geometry =
               res?.payload?.data?.data?.items?.[0]?.order_payload?.geometry;
            const featureId = `${AOI_FEATURE_KEY}-${res?.payload?.data?.data?.order_number}`;
            const fill = "rgba(255, 255, 0, 0.9)";
            const stroke = "rgba(255, 255, 0, 1)";



            dispatch(updateFeature({ id: featureId, geometry, fill, stroke }));
            changeSourceData(engineMap, [{ id: featureId, geometry, fill, stroke }]);
            flyToGeometry(geometry, engineMap);
         }

         handleGetSchema(res?.payload?.data?.data);
      } catch (error)
      {
         console.error("Failed to fetch order data:", error);
      } finally
      {
         setLoading(false);
      }
   };


   // Validation: returns { isValid: boolean, errors: string[] }
   const getValidationErrors = () =>
   {
      const errors: string[] = [];

      if (!schema?.steps) return errors;

      schema.steps.forEach((step: any) =>
      {
         const requiredFields = step?.schema?.required || [];
         const uiOptions = step?.ui?.options || {};

         requiredFields.forEach((fieldKey: string) =>
         {
            const value = formData[fieldKey];
            const fieldConfig = uiOptions[fieldKey];
            const fieldLabel = fieldConfig?.title || fieldKey;

            // check empty
            const isEmpty =
               value === undefined ||
               value === null ||
               value === "" ||
               (Array.isArray(value) && value.length === 0) ||
               (typeof value === "object" &&
                  !Array.isArray(value) &&
                  Object.keys(value).length === 0);

            if (isEmpty)
            {
               errors.push(`${t(fieldLabel)} ${t("is required")}`);
               return;
            }

            // special case: date range
            if (fieldKey === "date_from" || fieldKey === "date_to")
            {
               const fromVal = formData?.date_from;
               const toVal = formData?.date_to;

               if (fromVal && toVal)
               {
                  const fromDate = dayjs(fromVal);
                  const toDate = dayjs(toVal);

                  if (fromDate.isAfter(toDate))
                  {
                     errors.push(t("Date from must be before date to"));
                  }
               }
            }
         });
      });

      return errors;
   };

   const validationErrors = useMemo(
      () => getValidationErrors(),
      [formData, schema],
   );
   const isValid = validationErrors.length === 0;

   // Build field mapping from schema's body_template
   const buildFieldMapping = (orderData: any, stepSchema: any) =>
   {
      const mapping: Record<string, any> = {};

      // Get all field names from ui.order
      const fieldOrder = stepSchema?.ui?.order || [];
      const requestBody = orderData?.items?.[0]?.request_body || {};

      fieldOrder.forEach((field: string) =>
      {
         // Map field to value from order data
         // Priority order: request_body > top-level data
         if (field in requestBody)
         {
            mapping[field] = requestBody[field];
         } else if (field === "name" && orderData.name)
         {
            mapping[field] = orderData.name;
         } else if (field === "priority" && orderData.priority_id)
         {
            mapping[field] = orderData.priority_id;
         } else if (field === "reason" && orderData.reason)
         {
            mapping[field] = orderData.reason;
         } else if (field === "description" && orderData.description)
         {
            mapping[field] = orderData.description;
         } else if (field === "provider")
         {
            mapping[field] =
               orderData.provider?.code || orderData.provider?.name?.toLowerCase();
         } else if (field === "order_type")
         {
            mapping[field] = orderData.order_type || "tasking";
         } else
         {
            mapping[field] = undefined;
         }
      });

      return mapping;
   };

   // Transform order data to form data (dynamic, based on schema)
   const transformOrderToFormData = (orderData: any, workflowSchema: any) =>
   {
      if (!orderData || !workflowSchema) return {};

      const formData: Record<string, any> = {};

      workflowSchema.steps?.forEach((step: any) =>
      {
         const fieldOrder = step?.ui?.order || [];
         const fieldOptions = step?.ui?.options || {};

         const mapping = buildFieldMapping(orderData, step);

         fieldOrder.forEach((field: string) =>
         {
            let value = mapping[field];
            const config = fieldOptions[field];

            if (config?.widget === "date-picker" || config?.widget === "date")
            {
               if (value)
               {
                  if (typeof value === "number")
                  {
                     formData[field] = dayjs(value);
                  } else if (typeof value === "string")
                  {
                     formData[field] = dayjs(value);
                  } else
                  {
                     formData[field] = value;
                  }
               } else
               {
                  formData[field] = null;
               }
            } else if (config?.widget === "range" || config?.widget === "slider")
            {
               formData[field] =
                  value !== undefined && value !== null
                     ? parseFloat(value)
                     : (config.default ?? config.min ?? 0);
            } else if (
               (config?.widget === "select" || config?.widget === "radio") &&
               config.options
            )
            {
               // const optionValues = config.options.map((opt: any) => opt.value);
               // formData[field] = optionValues.includes(value)
               //    ? value
               //    : config.default || optionValues[0];

               const optionValues = config.options.map((opt: any) => opt.value);

               if (Array.isArray(value))
               {
                  // multiple select
                  const validValues = value.filter((v) => optionValues.includes(v));

                  formData[field] =
                     validValues.length > 0
                        ? validValues
                        : config.default || [optionValues[0]];
               } else
               {
                  // single select
                  formData[field] = optionValues.includes(value)
                     ? value
                     : config.default || optionValues[0];
               }
            } else
            {
               formData[field] = value;
            }
         });
      });

      return formData;
   };

   // Transform form data back to API payload (dynamic)

   const transformFormDataToOrderPayload = (
      formData: any,
      originalData: any,
      workflowSchema: any,
   ) =>
   {
      if (!workflowSchema) return {};

      const editableFields = getEditableFields(workflowSchema);

      const submissionStep =
         workflowSchema.steps?.find((s: any) => s.submit?.body_template) ||
         workflowSchema.steps?.[1];

      const bodyTemplate = submissionStep?.submit?.body_template;
      if (!bodyTemplate) return {};

      const requestOrder: Record<string, any> = {};

      Object.keys(bodyTemplate).forEach((key) =>
      {
         const fieldName =
            key.startsWith("${") && key.endsWith("}") ? key.slice(2, -1) : key;

         // chỉ lấy field editable
         if (!editableFields.has(fieldName)) return;

         let value = formData[fieldName];

         //  auto handle date (không hardcode field name)
         if (value && dayjs.isDayjs(value))
         {
            value = value.valueOf();
         }

         requestOrder[fieldName] = value;
      });

      return {
         ...requestOrder,
      };
   };

   const handleGetSchema = async (data: any) =>
   {
      try
      {
         setLoadingSchema(true);

         const providerId = data?.items?.[0]?.provider_code;

         if (!providerId)
         {
            console.warn("Provider ID not found in order data");
            setLoadingSchema(false);
            return;
         }

         const res = await dispatch(
            // @ts-ignore
            getSchemaOrderAsync({
               provider_id: providerId,
               type: "order_tasking",
            }),
         ).unwrap();
         setSchema(res?.data?.data?.workflow ?? {});
         setLoadingSchema(false);
      } catch (error)
      {
         console.error("Failed to fetch schema:", error);
         setLoadingSchema(false);
         showError(t("Failed to load form schema"));
      } finally
      {
         setLoading(false);
      }
   };

   const handleConfirm = async () =>
   {
      toggleConfirm();
      try
      {
         setLoading(true);
         const payload = transformFormDataToOrderPayload(formData, data, schema);

         const result = await dispatch(
            updateOrderAsync({ ...payload, id: id as string }),
         );
         const res = result as any;

         if (
            res?.payload?.status === ESttausCode.OK ||
            res?.payload?.status === ESttausCode.CREATED
         )
         {
            showSuccess(t("Update successfully"));
            navigate(-1);
         } else
         {
            showError(t("Update failed"));
         }
      } catch (error)
      {
         console.error("Failed to update order:", error);
         showError(t("Update failed"));
      } finally
      {
         setLoading(false);
      }
   };

   const getEditableFields = (workflowSchema: any) =>
   {
      const editableFields = new Set<string>();

      workflowSchema?.steps?.forEach((step: any) =>
      {
         const options = step?.ui?.options || {};

         Object.keys(options).forEach((key) =>
         {
            const config = options[key];

            if ((config?.canEdit ?? config?.can_edit) === true)
            {
               editableFields.add(key);
            }
         });
      });

      return editableFields;
   };
   const drawSource = engineMap?.getSource(DRAW_SOURCE_ID);
   console.log(drawSource, 'drawSource');

   // eslint-disable-next-line react-hooks/exhaustive-deps
   useEffect(() =>
   {
      if (!engineMap || !id) return;

      getData();
   }, [id, engineMap]);



   useEffect(() =>
   {
      return () =>
      {
         dispatch(clearFeatures());
      };
   }, []);

   useEffect(() =>
   {
      const basemaps = BASEMAPS;
      dispatch(setBasemapList(basemaps));
      dispatch(changeBasemap(basemaps[0]));
   }, []);

   // When schema loads, re-transform form data
   // eslint-disable-next-line react-hooks/exhaustive-deps
   // eslint-disable-next-line react-hooks/exhaustive-deps
   useEffect(() =>
   {
      if (
         schema &&
         Object.keys(schema).length > 0 &&
         data &&
         Object.keys(data).length > 0
      )
      {
         setFormData(transformOrderToFormData(data, schema));
      }
   }, [schema, data]);

   // Build formStep from workflow schema
   const formSteps: WorkflowStep[] = useMemo(() =>
   {
      if (!schema || !schema.steps) return [];

      return schema.steps
         .filter((step: any) => step?.schema && step?.ui)
         .map((step: any) => ({
            title: step?.title,
            step_id: step.step_id,
            schema: {
               required: step.schema?.required || [],
            },
            ui: {
               order: step.ui?.order || [],
               options: step.ui?.options || {},
            },
         }));
   }, [schema]);

   return (
      <ContentLayoutManagement
         header={
            <HeaderContent
               breadcrumb={[
                  {
                     title: t("Tasking Orders"),
                     path: `/order/management-tasking${location.search}`,
                  },
                  {
                     title: data?.name,
                     path: `/order/management-tasking/${data?.id}${location.search}`,
                  },
                  {
                     title: t("Edit"),
                  },
               ]}
            />
         }
      >
         <div className="h-full flex bg-[var(--neutral-1000)] text-white">
            {/* Left Panel - Form */}

            <div className="h-full relative w-5/12 flex flex-col border-r border-[#26324a]">
               {/* Scrollable Form Container */}

               <Spin spinning={loadingSchema || loading}>
                  <div
                     className="pt-6 overflow-auto"
                     style={{ height: "calc(100% - 65px)" }}
                  >
                     {loadingSchema ? (
                        <div className="p-4 text-gray-400 text-center">
                           {t("Loading form...")}
                        </div>
                     ) : formSteps.length > 0 && !loadingSchema ? (
                        formSteps.map((step, index) => (
                           <div key={step?.step_id}>
                              <p className="text-xl font-semibold px-6 pb-4">
                                 {t(step?.title)}
                              </p>
                              <UnifiedDynamicForm
                                 step={step}
                                 formData={formData}
                                 setFormData={setFormData}
                                 type="edit"
                                 display="grid"
                              />

                              {index !== formSteps.length - 1 && <Divider />}
                           </div>
                        ))
                     ) : (
                        <div className="p-4 text-gray-400 text-center">
                           {t("No form schema available")}
                        </div>
                     )}
                  </div>
               </Spin>
               <div
                  className="flex absolute bottom-0 w-full justify-between px-6  py-4"
                  style={{ borderTop: "1px solid var(--divider-1)" }}
               >
                  <div>
                     <span className="w-[151px] block">
                        <Button
                           onClick={() => navigate(-1)}
                           className="w-full border border-[var(--primary-4)] !bg-[none] !text-[var(--primary-4)]"
                           style={{ background: "none" }}
                        >
                           {t("Back")}
                        </Button>
                     </span>
                  </div>
                  <div className="flex gap-2">
                     {isValid ? (
                        <Button
                           className="w-full !border-none !bg-[var(--primary-4)] text-[var(--primary-950)]"
                           onClick={() => toggleConfirm()}
                           disabled={loadingSchema || !formSteps}
                        >
                           {t("Save request")}
                        </Button>
                     ) : (
                        <Tooltip
                           title={
                              <ul className="list-disc pl-4">
                                 {validationErrors.map((err, idx) => (
                                    <li key={idx}>{err}</li>
                                 ))}
                              </ul>
                           }
                           placement="top"
                        >
                           <Button
                              className="btn-primary"
                              // disabled
                              style={{ cursor: "not-allowed" }}
                           >
                              {t("Save request")}
                           </Button>
                        </Tooltip>
                     )}
                  </div>
               </div>
            </div>

            {/* Right Panel - Map */}
            <div className="flex-1 relative">
               {selectedBasemap && (
                  <ImagesMapVMRS
                     mapType={mapType}
                     view={mapView}
                     onSetEngineMap={(map: any) =>
                     {
                        if (!map) return;
                        // setEngineMap only after map is fully loaded so fly2GeometryList has a ready engine
                        map.loaded()
                           ? setEngineMap(map)
                           : map.once("load", () => setEngineMap(map));
                     }}
                  />
               )}
            </div>

            {shouldConfirm && (
               <CommonComponents.ModalUI
                  title={t("Confirm")}
                  open={openConfirm}
                  toggle={toggleConfirm}
                  handleConfirm={handleConfirm}
               >
                  <p>{t("Are you sure you want to save this request?")}</p>
               </CommonComponents.ModalUI>
            )}
         </div>
      </ContentLayoutManagement>
   );
}
